import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  User, Fingerprint, Landmark, Briefcase, FileText, MapPin, Mail, ShieldCheck,
  ChevronRight, ChevronLeft, Loader2, Check, Upload, X, LogOut,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { api, tokenStore, uploadFile } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

// ── Shapes returned by GET /merchant/onboarding ────────────────────────────────

type StepKey = 'email' | 'pan' | 'aadhaar' | 'bank' | 'gstin' | 'pickup' | 'agreement';

interface OnboardingState {
  merchant: { id: number; companyName: string; ownerName: string | null; email: string; mobile: string; status: string; emailVerified: boolean; mobileVerified: boolean };
  onboardingStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'SUBMITTED' | 'COMPLETED';
  onboardingStep: number;
  required: boolean;
  canSubmit: boolean;
  missing: StepKey[];
  steps: Array<{ key: StepKey; title: string; subtitle: string; index: number; done: boolean }>;
  values: any;
}

const STEP_ICONS: Record<StepKey, any> = {
  email: <Mail size={18} />,
  pan: <User size={18} />,
  aadhaar: <Fingerprint size={18} />,
  bank: <Landmark size={18} />,
  gstin: <Briefcase size={18} />,
  pickup: <MapPin size={18} />,
  agreement: <FileText size={18} />,
};

export function OnboardingWizard() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [active, setActive] = useState<number | null>(null);

  const { data: state, isLoading } = useQuery<OnboardingState>({
    queryKey: ['merchant', 'onboarding'],
    queryFn: async () => (await api.get('/merchant/onboarding')).data,
    enabled: !!tokenStore.get(),
  });

  // The store is the source of truth for the redirect in MerchantLayout, and the server may disagree
  // with it — an admin can reopen an account, or the merchant may have finished in another tab. Keep
  // the two in step so neither screen bounces the user back and forth with the other.
  useEffect(() => {
    if (!state || !user || user.kind !== 'merchant') return;
    if (user.onboardingStatus !== state.onboardingStatus || user.emailVerified !== state.merchant.emailVerified) {
      setUser({ ...user, onboardingStatus: state.onboardingStatus, emailVerified: state.merchant.emailVerified });
    }
  }, [state, user, setUser]);

  // Open on the first UNFINISHED step rather than always on step 1 — a merchant who comes back
  // after a break should land where they stopped, not scroll past work they have already done.
  useEffect(() => {
    if (active !== null || !state) return;
    const firstOpen = state.steps.findIndex((s) => !s.done);
    setActive(firstOpen === -1 ? state.steps.length - 1 : firstOpen);
  }, [state, active]);

  const submit = useMutation({
    mutationFn: async () => (await api.post('/merchant/onboarding/submit')).data,
    onSuccess: () => {
      toast.success('Onboarding submitted — welcome aboard!');
      // Flip the stored status BEFORE navigating, or MerchantLayout reads the stale IN_PROGRESS and
      // bounces straight back here.
      if (user && user.kind === 'merchant') setUser({ ...user, onboardingStatus: 'SUBMITTED' });
      // The server gate reads a fresh value immediately after submit, but every cached query in the
      // portal was answered with a 403 while we were blocked. Clearing the whole cache is the
      // cheapest way to guarantee the dashboard loads real data rather than replaying those errors.
      qc.clear();
      nav('/dashboard', { replace: true });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not submit'),
  });

  if (!tokenStore.get()) return <Navigate to="/login" replace />;
  // Reachable by typing the URL. A merchant who is already through should not be shown a wizard they
  // cannot edit — but only redirect once the server has confirmed it, not off the persisted store.
  if (state && !state.required) return <Navigate to="/dashboard" replace />;

  if (isLoading || !state || active === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  const step = state.steps[active];
  const doneCount = state.steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / state.steps.length) * 100);
  const onSaved = (next?: boolean) => {
    qc.invalidateQueries({ queryKey: ['merchant', 'onboarding'] });
    if (next && active < state.steps.length - 1) setActive(active + 1);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-start justify-between gap-4 mb-7">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Seller Onboarding</h1>
            <p className="text-slate-500 mt-1">Complete your verification to start shipping with Gaint Logistics</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${state.canSubmit ? 'bg-success/10 text-success' : 'bg-amber-50 text-amber-700'}`}>
              {state.canSubmit ? 'Ready to submit' : 'Incomplete'}
            </span>
            <button onClick={logout} title="Sign out"
              className="text-slate-400 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100">
              <LogOut size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5 items-start">
          {/* Step rail */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="h-1.5 bg-slate-100">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="p-3">
              {state.steps.map((s, i) => {
                const isActive = i === active;
                return (
                  <button key={s.key} onClick={() => setActive(i)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition ${isActive ? 'bg-primary-50' : 'hover:bg-slate-50'}`}>
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      s.done ? 'bg-success text-white' : isActive ? 'bg-primary text-white' : 'bg-slate-100 text-slate-400'}`}>
                      {s.done ? <Check size={17} /> : STEP_ICONS[s.key]}
                    </span>
                    <span className="min-w-0">
                      <span className={`block text-sm font-semibold truncate ${isActive ? 'text-primary' : 'text-slate-800'}`}>{s.title}</span>
                      <span className="block text-xs text-slate-400 truncate">{s.subtitle}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400 flex items-center gap-1.5">
              <ShieldCheck size={14} /> Your documents are stored encrypted
            </div>
          </div>

          {/* Active step */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
              <span className="w-11 h-11 rounded-xl bg-primary-50 text-primary flex items-center justify-center">{STEP_ICONS[step.key]}</span>
              <div>
                <h2 className="text-lg font-bold text-slate-900">{step.title}</h2>
                <p className="text-sm text-slate-500">{STEP_BLURB[step.key]}</p>
              </div>
            </div>

            <div className="px-6 py-6">
              {step.key === 'email' && <EmailStep state={state} onSaved={onSaved} />}
              {step.key === 'pan' && <PanStep state={state} onSaved={onSaved} />}
              {step.key === 'aadhaar' && <AadhaarStep state={state} onSaved={onSaved} />}
              {step.key === 'bank' && <BankStep state={state} onSaved={onSaved} />}
              {step.key === 'gstin' && <GstinStep state={state} onSaved={onSaved} />}
              {step.key === 'pickup' && <PickupStep state={state} onSaved={onSaved} />}
              {step.key === 'agreement' && <AgreementStep state={state} onSaved={onSaved} />}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
              <button onClick={() => setActive(Math.max(0, active - 1))} disabled={active === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1.5">
                <ChevronLeft size={16} /> Previous
              </button>
              {active < state.steps.length - 1 ? (
                <button onClick={() => setActive(active + 1)}
                  className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary-700 flex items-center gap-1.5">
                  Next Step <ChevronRight size={16} />
                </button>
              ) : (
                <button onClick={() => submit.mutate()} disabled={!state.canSubmit || submit.isPending}
                  title={state.canSubmit ? '' : 'Finish every step first'}
                  className="px-5 py-2 rounded-lg text-sm font-medium bg-success text-white hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5">
                  {submit.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Submit &amp; Start Shipping
                </button>
              )}
            </div>
          </div>
        </div>

        {!state.canSubmit && (
          <p className="text-center text-xs text-slate-400 mt-5">
            Still to do: {state.missing.map((k) => state.steps.find((s) => s.key === k)?.title).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}

const STEP_BLURB: Record<StepKey, string> = {
  email: 'We sent a 6-digit code to your registered email address.',
  pan: 'Your PAN can be submitted now and is verified by our team afterwards.',
  aadhaar: 'Used to confirm the identity of the account holder.',
  bank: 'Where your COD remittances are paid out. Make sure it matches your PAN.',
  gstin: 'Required to raise GST invoices in your business name.',
  pickup: 'Where our courier partner collects your shipments.',
  agreement: 'Read and accept the terms to finish.',
};

// ── Shared bits ────────────────────────────────────────────────────────────────

function Field({ label, required, hint, error, children, wide }: { label: string; required?: boolean; hint?: string; error?: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {error ? <div className="text-xs text-rose-600 mt-1">{error}</div>
        : hint ? <div className="text-xs text-slate-400 mt-1">{hint}</div> : null}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function SaveBar({ busy, done, onSave, label = 'Save & Continue' }: { busy: boolean; done: boolean; onSave: () => void; label?: string }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <button onClick={onSave} disabled={busy}
        className="px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
        {busy ? <Loader2 size={16} className="animate-spin" /> : null} {label}
      </button>
      {done && <span className="text-sm text-success flex items-center gap-1"><Check size={16} /> Saved</span>}
    </div>
  );
}

/**
 * Document picker. Uploads straight to R2 and keeps only the returned ref — the same contract the
 * POD and weight-dispute screens use, so a scanned PAN card never travels as base64 inside a JSON
 * body or gets stored inline in a column that every list query then reads.
 */
function DocUpload({ value, onChange, label, accept = 'image/*,application/pdf' }: { value?: string | null; onChange: (ref: string | null) => void; label: string; accept?: string }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = async (f: File | null) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('File must be under 8 MB'); return; }
    setBusy(true);
    try {
      const { ref } = await uploadFile(f, 'merchant_kyc', f.name);
      onChange(ref);
      toast.success(`${label} uploaded`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => pick(e.target.files?.[0] ?? null)} />
      {value ? (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-success/40 bg-success/5 text-sm">
          <Check size={16} className="text-success shrink-0" />
          <span className="flex-1 truncate text-slate-700">{value.split('/').pop()}</span>
          <button onClick={() => onChange(null)} className="text-slate-400 hover:text-rose-600" title="Remove"><X size={15} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 hover:border-primary hover:text-primary disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload {label}
        </button>
      )}
    </div>
  );
}

/** POST a step and refresh the wizard. Returns [run, busy]. */
function useStep(path: string, onSaved: (next?: boolean) => void) {
  const m = useMutation({
    mutationFn: async (body: any) => (await api.post(`/merchant/onboarding/${path}`, body)).data,
    onSuccess: () => { toast.success('Saved'); onSaved(true); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });
  return m;
}

type StepProps = { state: OnboardingState; onSaved: (next?: boolean) => void };

// ── Step 1: email OTP ──────────────────────────────────────────────────────────

function EmailStep({ state, onSaved }: StepProps) {
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const verified = state.merchant.emailVerified;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = useMutation({
    mutationFn: async () => (await api.post('/merchant/onboarding/email/send-otp')).data,
    onSuccess: () => { toast.success('Code sent — check your inbox'); setCooldown(60); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not send the code'),
  });
  const verify = useMutation({
    mutationFn: async () => (await api.post('/merchant/onboarding/email/verify-otp', { code })).data,
    onSuccess: () => { toast.success('Email verified'); onSaved(true); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Verification failed'),
  });

  if (verified) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg bg-success/5 border border-success/30">
        <Check size={20} className="text-success" />
        <div>
          <div className="font-semibold text-slate-800">{state.merchant.email} is verified</div>
          <div className="text-sm text-slate-500">Shipment alerts, invoices and remittance advice will be sent here.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md space-y-4">
      <p className="text-sm text-slate-600">
        We sent a 6-digit code to <b className="text-slate-900">{state.merchant.email}</b>. Enter it below to confirm the address is yours.
      </p>
      <Field label="Verification code" required hint="The code expires 15 minutes after it is sent.">
        <input value={code} inputMode="numeric" maxLength={6} autoFocus
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && verify.mutate()}
          className="input w-full text-center text-2xl tracking-[0.5em] font-mono" placeholder="••••••" />
      </Field>
      <div className="flex items-center gap-3">
        <button onClick={() => verify.mutate()} disabled={code.length !== 6 || verify.isPending}
          className="px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
          {verify.isPending ? <Loader2 size={16} className="animate-spin" /> : null} Verify Email
        </button>
        <button onClick={() => send.mutate()} disabled={send.isPending || cooldown > 0}
          className="text-sm text-primary font-medium hover:underline disabled:text-slate-400 disabled:no-underline">
          {cooldown > 0 ? `Resend in ${cooldown}s` : send.isPending ? 'Sending…' : 'Send / resend code'}
        </button>
      </div>
      <p className="text-xs text-slate-400">
        Wrong address? Contact support at <a className="text-primary" href="mailto:it1@gaintlogistic.com">it1@gaintlogistic.com</a> — the email on the account cannot be changed here because it is your login.
      </p>
    </div>
  );
}

// ── Step 2: PAN ────────────────────────────────────────────────────────────────

function PanStep({ state, onSaved }: StepProps) {
  const v = state.values.pan ?? {};
  const [pan, setPan] = useState(v.panNumber ?? '');
  const [ownerName, setOwnerName] = useState(v.ownerName ?? state.merchant.ownerName ?? '');
  const [file, setFile] = useState<string | null>(v.panFile ?? null);
  const save = useStep('pan', onSaved);
  const valid = /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);

  return (
    <div className="space-y-5">
      <Grid>
        <Field label="Permanent Account Number (PAN)" required
          hint="10 characters exactly as printed on the card, e.g. ABCDE1234F"
          error={pan && !valid ? 'That does not look like a valid PAN' : undefined}>
          <input value={pan} maxLength={10} autoFocus
            onChange={(e) => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            className="input w-full font-mono tracking-widest uppercase" placeholder="ABCDE1234F" />
        </Field>
        <Field label="Name as per PAN" required hint="Must match the card — your bank account has to match it too.">
          <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
            className="input w-full" placeholder="Rajesh Kumar" />
        </Field>
        <Field label="PAN card image" wide hint="JPG, PNG or PDF, under 8 MB.">
          <DocUpload value={file} onChange={setFile} label="PAN card" />
        </Field>
      </Grid>
      <SaveBar busy={save.isPending} done={!!v.panNumber}
        onSave={() => {
          if (!valid) return toast.error('Enter a valid 10-character PAN');
          if (!ownerName.trim()) return toast.error('Enter the name as printed on the PAN');
          save.mutate({ panNumber: pan, ownerName: ownerName.trim(), ...(file ? { panFile: file } : {}) });
        }} />
    </div>
  );
}

// ── Step 3: Aadhaar ────────────────────────────────────────────────────────────

function AadhaarStep({ state, onSaved }: StepProps) {
  const v = state.values.aadhaar ?? {};
  const [aadhaar, setAadhaar] = useState(v.aadharNumber ?? '');
  const [file, setFile] = useState<string | null>(v.aadharFile ?? null);
  const save = useStep('aadhaar', onSaved);
  const valid = /^[2-9][0-9]{11}$/.test(aadhaar);
  // Displayed in 4-4-4 groups, which is how it is printed and how people read it back; the value
  // posted is always the bare 12 digits.
  const pretty = aadhaar.replace(/(\d{4})(?=\d)/g, '$1 ').trim();

  return (
    <div className="space-y-5">
      <Grid>
        <Field label="Aadhaar Number" required
          hint="12 digits, no spaces needed"
          error={aadhaar.length === 12 && !valid ? 'An Aadhaar number never starts with 0 or 1' : undefined}>
          <input value={pretty} inputMode="numeric" maxLength={14}
            onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, '').slice(0, 12))}
            className="input w-full font-mono tracking-widest" placeholder="1234 5678 9012" />
        </Field>
        <Field label="Aadhaar image" hint="Front side. JPG, PNG or PDF.">
          <DocUpload value={file} onChange={setFile} label="Aadhaar" />
        </Field>
      </Grid>
      <SaveBar busy={save.isPending} done={!!v.aadharNumber}
        onSave={() => {
          if (!valid) return toast.error('Enter a valid 12-digit Aadhaar number');
          save.mutate({ aadharNumber: aadhaar, ...(file ? { aadharFile: file } : {}) });
        }} />
    </div>
  );
}

// ── Step 4: Bank ───────────────────────────────────────────────────────────────

function BankStep({ state, onSaved }: StepProps) {
  const v = state.values.bank ?? {};
  const [f, setF] = useState({
    accountHolderName: v.accountHolderName ?? state.values.pan?.ownerName ?? state.merchant.ownerName ?? '',
    accountNumber: v.accountNumber ?? '',
    confirmAccountNumber: v.accountNumber ?? '',
    bankName: v.bankName ?? '',
    branchName: v.branchName ?? '',
    ifscCode: v.ifscCode ?? '',
  });
  const [file, setFile] = useState<string | null>(v.cancelledChequeFile ?? null);
  const save = useStep('bank', onSaved);
  const set = (k: keyof typeof f) => (e: any) => setF({ ...f, [k]: e.target.value });
  const ifscOk = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(f.ifscCode);
  const mismatch = !!f.confirmAccountNumber && f.accountNumber !== f.confirmAccountNumber;

  return (
    <div className="space-y-5">
      <Grid>
        <Field label="Account Holder Name" required hint="Exactly as it appears on your bank records.">
          <input value={f.accountHolderName} onChange={set('accountHolderName')} className="input w-full" placeholder="Rajesh Kumar" />
        </Field>
        <Field label="Bank Name" required>
          <input value={f.bankName} onChange={set('bankName')} className="input w-full" placeholder="HDFC Bank" />
        </Field>
        <Field label="Account Number" required>
          <input value={f.accountNumber} inputMode="numeric" maxLength={18}
            onChange={(e) => setF({ ...f, accountNumber: e.target.value.replace(/\D/g, '') })}
            className="input w-full font-mono" placeholder="50100123456789" />
        </Field>
        {/* Asked twice on purpose: a mistyped account number sends a COD remittance to a stranger,
            and nothing downstream can detect it. */}
        <Field label="Re-enter Account Number" required error={mismatch ? 'The two account numbers do not match' : undefined}>
          <input value={f.confirmAccountNumber} inputMode="numeric" maxLength={18} onPaste={(e) => e.preventDefault()}
            onChange={(e) => setF({ ...f, confirmAccountNumber: e.target.value.replace(/\D/g, '') })}
            className="input w-full font-mono" placeholder="50100123456789" />
        </Field>
        <Field label="IFSC Code" required hint="11 characters, 5th is always a zero"
          error={f.ifscCode && !ifscOk ? 'That does not look like a valid IFSC' : undefined}>
          <input value={f.ifscCode} maxLength={11}
            onChange={(e) => setF({ ...f, ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
            className="input w-full font-mono uppercase" placeholder="HDFC0001234" />
        </Field>
        <Field label="Branch Name">
          <input value={f.branchName} onChange={set('branchName')} className="input w-full" placeholder="Sector 62, Noida" />
        </Field>
        <Field label="Cancelled cheque / passbook" wide hint="Speeds up verification considerably.">
          <DocUpload value={file} onChange={setFile} label="cancelled cheque" />
        </Field>
      </Grid>
      <SaveBar busy={save.isPending} done={!!v.accountNumber}
        onSave={() => {
          if (!f.accountHolderName.trim() || !f.bankName.trim()) return toast.error('Account holder and bank name are required');
          if (!/^\d{9,18}$/.test(f.accountNumber)) return toast.error('Account number must be 9–18 digits');
          if (mismatch || !f.confirmAccountNumber) return toast.error('Re-enter the account number to confirm it');
          if (!ifscOk) return toast.error('Enter a valid IFSC code');
          const { confirmAccountNumber, ...payload } = f;
          save.mutate({ ...payload, ...(file ? { cancelledChequeFile: file } : {}) });
        }} />
    </div>
  );
}

// ── Step 5: GSTIN ──────────────────────────────────────────────────────────────

function GstinStep({ state, onSaved }: StepProps) {
  const v = state.values.gstin ?? {};
  const [noGstin, setNoGstin] = useState(!!v.noGstin);
  const [gst, setGst] = useState(v.noGstin ? '' : (v.gstNumber ?? ''));
  const [file, setFile] = useState<string | null>(v.gstFile ?? null);
  const save = useStep('gstin', onSaved);
  const valid = /^[0-3][0-9][A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gst);
  const pan = state.values.pan?.panNumber;
  const panMismatch = valid && pan && gst.slice(2, 12) !== pan;

  return (
    <div className="space-y-5">
      <label className="flex items-center gap-2.5 text-sm text-slate-700 p-3 rounded-lg bg-slate-50 border border-slate-200 cursor-pointer">
        <input type="checkbox" checked={noGstin} onChange={(e) => setNoGstin(e.target.checked)} className="rounded" />
        <span>I am <b>not</b> GST registered (composition scheme or below the turnover threshold)</span>
      </label>

      {!noGstin && (
        <Grid>
          <Field label="GSTIN" required wide
            hint="15 characters. Characters 3–12 are your PAN."
            error={gst && !valid ? 'That does not look like a valid GSTIN'
              : panMismatch ? `This GSTIN contains PAN ${gst.slice(2, 12)} but you entered ${pan} earlier` : undefined}>
            <input value={gst} maxLength={15}
              onChange={(e) => setGst(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              className="input w-full font-mono tracking-widest uppercase" placeholder="29ABCDE1234F1Z5" />
          </Field>
          <Field label="GST certificate" wide hint="The registration certificate PDF from the GST portal.">
            <DocUpload value={file} onChange={setFile} label="GST certificate" />
          </Field>
        </Grid>
      )}

      <SaveBar busy={save.isPending} done={!!v.gstNumber}
        onSave={() => {
          if (noGstin) return save.mutate({ noGstin: true });
          if (!valid) return toast.error('Enter a valid 15-character GSTIN');
          if (panMismatch) return toast.error('The GSTIN and the PAN you entered do not match');
          save.mutate({ gstNumber: gst, ...(file ? { gstFile: file } : {}) });
        }} />
    </div>
  );
}

// ── Step 6: pickup address ─────────────────────────────────────────────────────

function PickupStep({ state, onSaved }: StepProps) {
  const v = state.values.pickup ?? {};
  const [f, setF] = useState({
    name: v.name ?? 'Primary Pickup',
    address: v.address ?? '',
    city: v.city ?? '',
    state: v.state ?? '',
    pincode: v.pincode ?? '',
    contactName: v.contactName ?? state.merchant.ownerName ?? '',
    contactPhone: v.contactPhone ?? state.merchant.mobile ?? '',
  });
  const save = useStep('pickup', onSaved);
  const set = (k: keyof typeof f) => (e: any) => setF({ ...f, [k]: e.target.value });

  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get('/states')).data as Array<{ id: number; name: string }>,
    staleTime: 60 * 60 * 1000,
  });

  return (
    <div className="space-y-5">
      <Grid>
        <Field label="Location name" required hint="A label you will recognise, e.g. “Noida Warehouse”.">
          <input value={f.name} onChange={set('name')} className="input w-full" placeholder="Primary Pickup" />
        </Field>
        <Field label="Contact person" required>
          <input value={f.contactName} onChange={set('contactName')} className="input w-full" placeholder="Rajesh Kumar" />
        </Field>
        <Field label="Full address" required wide hint="Building, street and area — the rider has to find it.">
          <input value={f.address} onChange={set('address')} className="input w-full" placeholder="Plot 12, Sector 63, Block A" />
        </Field>
        <Field label="City" required>
          <input value={f.city} onChange={set('city')} className="input w-full" placeholder="Noida" />
        </Field>
        <Field label="State" required>
          {states?.length ? (
            <select value={f.state} onChange={set('state')} className="input w-full">
              <option value="">Select a state</option>
              {states.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          ) : (
            <input value={f.state} onChange={set('state')} className="input w-full" placeholder="Uttar Pradesh" />
          )}
        </Field>
        <Field label="Pincode" required>
          <input value={f.pincode} inputMode="numeric" maxLength={6}
            onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/\D/g, '') })}
            className="input w-full font-mono" placeholder="201301" />
        </Field>
        <Field label="Pickup contact number" required hint="The courier calls this number to arrange collection.">
          <input value={f.contactPhone} inputMode="numeric" maxLength={10}
            onChange={(e) => setF({ ...f, contactPhone: e.target.value.replace(/\D/g, '') })}
            className="input w-full font-mono" placeholder="9876543210" />
        </Field>
      </Grid>
      <p className="text-xs text-slate-400">
        Returned parcels come back to this address by default. You can add more pickup locations, and a different return address, from Pickup Locations once you are in.
      </p>
      <SaveBar busy={save.isPending} done={!!v.name}
        onSave={() => {
          if (!f.name.trim() || !f.address.trim() || !f.city.trim() || !f.state.trim()) return toast.error('Fill in the address, city and state');
          if (!/^[1-9]\d{5}$/.test(f.pincode)) return toast.error('Enter a valid 6-digit pincode');
          if (!/^[6-9]\d{9}$/.test(f.contactPhone)) return toast.error('Enter a valid 10-digit mobile number');
          if (!f.contactName.trim()) return toast.error('Enter a contact person');
          save.mutate(f);
        }} />
    </div>
  );
}

// ── Step 7: agreement ──────────────────────────────────────────────────────────

function AgreementStep({ state, onSaved }: StepProps) {
  const v = state.values.agreement ?? {};
  const [accepted, setAccepted] = useState(!!v.accepted);
  const save = useStep('agreement', onSaved);

  const acceptedOn = useMemo(
    () => (v.acceptedAt ? new Date(v.acceptedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : null),
    [v.acceptedAt],
  );

  return (
    <div className="space-y-5">
      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 space-y-3">
        <p><b>1. Services.</b> Gaint Logistics arranges pickup, transport and delivery of your shipments through its courier partners. Bookings are subject to each partner's serviceability, weight and dimension limits.</p>
        <p><b>2. Accuracy of information.</b> You confirm the PAN, Aadhaar, GSTIN and bank details you have supplied belong to you or to the business you represent, and that you will keep them up to date.</p>
        <p><b>3. Weight and dimensions.</b> Freight is charged on the higher of actual and volumetric weight as measured by the courier partner. Where a discrepancy is raised you may dispute it with evidence through the portal.</p>
        <p><b>4. Remittance.</b> COD proceeds are remitted to the verified bank account above, net of freight and applicable charges, on the remittance cycle agreed for your account.</p>
        <p><b>5. Prohibited goods.</b> You will not tender any item restricted or prohibited by law or by the carrier, and you remain liable for any penalty arising from doing so.</p>
        <p><b>6. Liability.</b> Liability for loss or damage is limited to the carrier's declared liability for the shipment concerned, unless additional cover was purchased at booking.</p>
        <p><b>7. Suspension.</b> Accounts may be suspended for unpaid dues, fraudulent bookings or repeated breach of these terms.</p>
        <p className="text-xs text-slate-400">Version {v.version ?? 'v1.0'}. The complete Terms of Service and Privacy Policy are available on our website and form part of this agreement.</p>
      </div>

      <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 rounded" />
        <span>I have read and accept the Terms &amp; Conditions and the Privacy Policy, and I confirm I am authorised to act for <b>{state.merchant.companyName}</b>.</span>
      </label>

      {acceptedOn && <p className="text-xs text-slate-400">Accepted on {acceptedOn}.</p>}

      <SaveBar busy={save.isPending} done={!!v.accepted} label="Accept & Continue"
        onSave={() => {
          if (!accepted) return toast.error('Tick the box to accept the terms');
          save.mutate({ accepted: true });
        }} />
    </div>
  );
}
